import { type RefObject, useEffect, useRef, useState } from 'react';

/**
 * Observes a container element's inline size and returns `true` when
 * the container is **narrower** than `minWidthPx`.
 *
 * This mirrors CSS `@container (min-width: Xpx)` — the text is hidden
 * (icon-only) when the container is below the threshold.
 *
 * Usage:
 * ```tsx
 * const [containerRef, isIconOnly] = useContainerQuery<HTMLDivElement>(470);
 * return <div ref={containerRef} className="@container">…</div>;
 * ```
 */
export function useContainerQuery<T extends HTMLElement>(
  minWidthPx: number
): [ref: RefObject<T>, isBelowThreshold: boolean] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const inlineSize =
          entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        setWidth(inlineSize);
      }
    });

    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  return [ref, width === null || width < minWidthPx];
}
